import { useState } from "react";
import { toast } from "sonner";
import {
  Download, Printer, FileText, ArrowLeft, Grid3x3, Layers3, Info, Scissors,
  Copy, ExternalLink, MessageSquareText, FileDown, Upload,
} from "lucide-react";
import { Header } from "@/components/Header";
import { DocxDropzone } from "@/components/DocxDropzone";
import { FlashcardPreview } from "@/components/FlashcardPreview";
import { A4SheetPreview } from "@/components/A4SheetPreview";
import { uploadDocx, loadSample, pdfUrl } from "@/lib/api";

export default function StudioPage({ dark, toggleTheme }) {
  const [deck, setDeck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState("cards"); // cards | sheet
  const [side, setSide] = useState("front"); // front | back
  const flip = "long";

  const JURIS_PROMPT =
    "Erzeuge mir 10 Karteikarten zu <Thema> (z.B. Kaufvertrag) und gib sie mir in der Struktur Frage/Antwort aus";

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(JURIS_PROMPT);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = JURIS_PROMPT;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch { /* ignore */ }
      document.body.removeChild(ta);
    }
    toast.success("Prompt kopiert.");
  };

  const handleUpload = async (file) => {
    setBusy(true);
    try {
      const data = await uploadDocx(file);
      setDeck(data);
      toast.success(`${data.cards.length} Karteikarten erkannt.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const handleSample = async () => {
    setBusy(true);
    try {
      const data = await loadSample();
      setDeck(data);
      toast.success(`Beispiel geladen · ${data.cards.length} Karten.`);
    } catch {
      toast.error("Beispiel konnte nicht geladen werden.");
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    if (!deck) return;
    toast.loading("PDF wird erzeugt…", { id: "pdf" });
    try {
      const res = await fetch(pdfUrl(deck.id, flip));
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "karteikarten.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF heruntergeladen.", { id: "pdf" });
    } catch {
      toast.error("PDF-Erzeugung fehlgeschlagen.", { id: "pdf" });
    }
  };

  return (
    <>
      <Header dark={dark} toggleTheme={toggleTheme} />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {!deck ? (
          <>
          <section data-testid="how-to-section">
            <div className="text-center">
              <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 dark:bg-emerald-950/40 px-3 py-1 text-xs font-semibold text-emerald-700 dark:text-emerald-300">
                Anleitung
              </span>
              <h1 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100">
                Wie gehst du vor?
              </h1>
              <p className="mt-2 text-slate-600 dark:text-slate-400 max-w-xl mx-auto">
                In drei Schritten von juris zu druckfertigen Lernkarten.
              </p>
            </div>

            <div className="mt-8 grid md:grid-cols-3 gap-5">
              {/* Schritt 1 */}
              <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 flex flex-col">
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center h-9 w-9 rounded-full bg-[#0F2942] dark:bg-blue-600 text-white font-display font-bold text-sm">1</span>
                  <MessageSquareText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="mt-4 font-display font-bold text-slate-900 dark:text-slate-100">Prompt bei juris eingeben</h3>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                  Öffne <span className="font-medium">juris.de</span> und gib diesen Prompt ein – ersetze
                  <span className="font-mono text-xs"> &lt;Thema&gt;</span> durch dein Thema:
                </p>
                <div className="mt-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 p-3">
                  <p className="text-xs font-mono text-slate-700 dark:text-slate-300 leading-relaxed">
                    „Erzeuge mir 10 Karteikarten zu &lt;Thema&gt; (z.B. Kaufvertrag) und gib sie mir in der Struktur Frage/Antwort aus"
                  </p>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    data-testid="copy-prompt-button"
                    onClick={copyPrompt}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    <Copy className="h-3.5 w-3.5" /> Prompt kopieren
                  </button>
                  <a
                    data-testid="open-juris-link"
                    href="https://juris.de"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-[#0F2942] dark:bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#1E3A5F] dark:hover:bg-blue-500"
                  >
                    juris.de öffnen <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>

              {/* Schritt 2 */}
              <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 flex flex-col">
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center h-9 w-9 rounded-full bg-[#0F2942] dark:bg-blue-600 text-white font-display font-bold text-sm">2</span>
                  <FileDown className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="mt-4 font-display font-bold text-slate-900 dark:text-slate-100">Als .docx herunterladen</h3>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                  Warte die Antwort von juris ab und lade das Ergebnis
                  <span className="font-medium"> inklusive Fundstellen</span> als
                  <span className="font-medium"> .docx</span>-Datei herunter.
                </p>
                <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                  Wichtig: Format „Frage / Antwort" beibehalten und die Quellen-Liste
                  („Quellen:") mit exportieren – daraus entstehen die QR-Codes & Links.
                </p>
              </div>

              {/* Schritt 3 */}
              <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 flex flex-col">
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center h-9 w-9 rounded-full bg-emerald-600 text-white font-display font-bold text-sm">3</span>
                  <Upload className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <h3 className="mt-4 font-display font-bold text-slate-900 dark:text-slate-100">Hier hochladen</h3>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                  Lade die .docx unten per Drag & Drop hoch. Wir erzeugen daraus das
                  druckfertige A6-Duplex-PDF – und du kannst direkt ins Quiz starten.
                </p>
                <button
                  data-testid="scroll-to-upload-button"
                  onClick={() => document.getElementById("upload-area")?.scrollIntoView({ behavior: "smooth", block: "center" })}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 self-start"
                >
                  <Upload className="h-3.5 w-3.5" /> Zum Upload
                </button>
              </div>
            </div>
          </section>

          <div className="mt-14 sm:mt-16 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 dark:bg-blue-950/40 px-3 py-1 text-xs font-semibold text-blue-700 dark:text-blue-300">
                <Scissors className="h-3.5 w-3.5" /> Duplexdruck · A4 → 4× A6
              </span>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100 leading-[1.05]">
                Von der <span className="text-blue-600 dark:text-blue-400">juris-KI</span> direkt zu Karteikarten.
              </h2>
              <p className="mt-4 text-slate-600 dark:text-slate-400 leading-relaxed max-w-lg">
                Lade dein .docx mit Fragen & Antworten hoch. Wir verteilen die Karten
                druckfertig auf A4 im A6-Querformat – Vorderseite mit Frage, Rückseite mit
                Antwort, Quellen und QR-Code. Beidseitig drucken, ausschneiden, lernen.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-slate-600 dark:text-slate-400">
                {["4 Karten pro A4-Blatt (A6 Querformat)", "Rückseiten für Duplexdruck gespiegelt", "Klickbare Quellen im PDF + QR-Code zur Quellen-Seite"].map((t) => (
                  <li key={t} className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-blue-500" /> {t}
                  </li>
                ))}
              </ul>
            </div>
            <div id="upload-area" className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/60 backdrop-blur p-6 sm:p-8 shadow-sm scroll-mt-24">
              <DocxDropzone onUpload={handleUpload} onSample={handleSample} busy={busy} />
            </div>
          </div>
          </>
        ) : (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-3 min-w-0">
                <button
                  data-testid="back-to-upload-button"
                  onClick={() => setDeck(null)}
                  className="h-9 w-9 grid place-items-center rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm font-medium text-slate-500 dark:text-slate-400">
                    <FileText className="h-4 w-4 shrink-0" />
                    <span className="truncate">{deck.filename}</span>
                  </div>
                  <span
                    data-testid="cards-count-badge"
                    className="mt-1 inline-block text-lg font-display font-bold text-slate-900 dark:text-slate-100"
                  >
                    {deck.cards.length} Karteikarten
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="print-pdf-button"
                  onClick={() => window.open(pdfUrl(deck.id, flip), "_blank")}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  <Printer className="h-4 w-4" /> Drucken
                </button>
                <button
                  data-testid="download-pdf-button"
                  onClick={downloadPdf}
                  className="inline-flex items-center gap-2 rounded-xl bg-[#0F2942] dark:bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#1E3A5F] dark:hover:bg-blue-500 shadow-sm"
                >
                  <Download className="h-4 w-4" /> PDF herunterladen
                </button>
              </div>
            </div>

            {/* Duplex-Hinweis */}
            <div className="mb-6 flex items-start gap-3 rounded-xl border border-blue-200 dark:border-blue-900/60 bg-blue-50/70 dark:bg-blue-950/30 px-4 py-3 text-sm text-blue-900 dark:text-blue-200">
              <Info className="h-4 w-4 mt-0.5 shrink-0" />
              <p>
                Druckeinstellung: <strong>Beidseitig · an langer Kante spiegeln</strong> · Querformat ·
                Skalierung 100&nbsp;%. Tipp: Erst 1 Testblatt drucken und gegen das Licht halten.
              </p>
            </div>

            {/* View-Umschalter */}
            <div className="flex flex-wrap items-center gap-2 mb-6">
              <div className="inline-flex rounded-xl border border-slate-200 dark:border-slate-700 p-1 bg-white dark:bg-slate-900">
                <button
                  data-testid="preview-mode-toggle-grid"
                  onClick={() => setView("cards")}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                    view === "cards" ? "bg-[#0F2942] dark:bg-blue-600 text-white" : "text-slate-600 dark:text-slate-300"
                  }`}
                >
                  <Grid3x3 className="h-4 w-4" /> Karten
                </button>
                <button
                  data-testid="preview-mode-toggle-sheet"
                  onClick={() => setView("sheet")}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                    view === "sheet" ? "bg-[#0F2942] dark:bg-blue-600 text-white" : "text-slate-600 dark:text-slate-300"
                  }`}
                >
                  <Layers3 className="h-4 w-4" /> A4-Vorschau
                </button>
              </div>

              {view === "sheet" && (
                <div className="inline-flex rounded-xl border border-slate-200 dark:border-slate-700 p-1 bg-white dark:bg-slate-900">
                  <button
                    data-testid="sheet-side-toggle-front"
                    onClick={() => setSide("front")}
                    className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
                      side === "front" ? "bg-slate-900 dark:bg-slate-200 text-white dark:text-slate-900" : "text-slate-600 dark:text-slate-300"
                    }`}
                  >
                    Vorderseite
                  </button>
                  <button
                    data-testid="sheet-side-toggle-back"
                    onClick={() => setSide("back")}
                    className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
                      side === "back" ? "bg-slate-900 dark:bg-slate-200 text-white dark:text-slate-900" : "text-slate-600 dark:text-slate-300"
                    }`}
                  >
                    Rückseite
                  </button>
                </div>
              )}
            </div>

            {view === "cards" ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {deck.cards.map((card) => (
                  <FlashcardPreview key={card.id} card={card} />
                ))}
              </div>
            ) : (
              <A4SheetPreview cards={deck.cards} side={side} flip={flip} />
            )}
          </div>
        )}
      </main>
    </>
  );
}
